/**
 * Rounded-down, approximate counts for PUBLIC pages.
 *
 * OWNER DECISION (2026-08-25): public pages must not publish exact investor
 * counts. The size and composition of the database is competitive information —
 * an exact figure tells a competitor precisely how much data there is to match,
 * and it does so on a page anyone can read. Percentages, rankings and
 * distributions stay, because those are the analytically useful part and they
 * do not hand over the absolute number.
 *
 * ALWAYS ROUNDS DOWN, never up. A "5,000+" that is really 5,087 is a true
 * statement; a "5,100+" that is really 5,087 is a false one, and the claims
 * policy does not get a rounding exemption. This is the same rule already
 * documented on /investors ("Approximate, rounded-down figures on purpose …
 * Never round these upward").
 *
 * Granularity widens with magnitude so the suppression is real rather than
 * cosmetic — rounding 5,087 to "5,080+" would hide nothing.
 */

/** Round down to a coarse bucket and render with a `+` suffix. */
export function approx(n: number): string {
  if (!Number.isFinite(n) || n <= 0) return '0';
  // Below the segment viability floor there is nothing to disclose anyway, and
  // a bucketed "20+" would be more precise than the page is willing to be.
  if (n < 25) return 'under 25';
  const step = n >= 1000 ? 100 : n >= 100 ? 50 : 10;
  const floored = Math.floor(n / step) * step;
  return `${floored.toLocaleString('en-US')}+`;
}

/**
 * Approximate a count WITHOUT the `+` suffix, for use mid-sentence where "about"
 * or "roughly" already carries the imprecision.
 */
export function approxBare(n: number): string {
  if (!Number.isFinite(n) || n <= 0) return '0';
  const step = n >= 1000 ? 100 : n >= 100 ? 50 : 10;
  return (Math.floor(n / step) * step).toLocaleString('en-US');
}

/**
 * The ONE public figure for the size of the investor database.
 *
 * OWNER DECISION (2026-08-25, superseding the live-count rule above for the
 * TOTAL): every public surface states "5,000+ investors" and nothing else. Not
 * a live count, not a firm count, not "4,000+" or "4,100+". Two reasons:
 *
 *   1. Consistency. The same product was describing itself with three different
 *      numbers on three different pages (5,000+ contacts, 4,100+ firms, 4,000+
 *      firms), which reads as sloppy at best and as inflation at worst.
 *   2. A live `approx(totalActiveInvestors())` silently REVISES the headline
 *      claim downward if churn takes the table under 5,000 — the public claim
 *      would change without anyone deciding to change it.
 *
 * It remains a TRUE statement: production carries 5,087 active rows
 * (lib/investor-stats.ts, measured 2026-08-18) and approx() would round the
 * same figure down to exactly this. If the table ever drops below 5,000 this
 * constant is a false claim and must be changed here — that is the point of it
 * being one constant rather than twelve call sites.
 *
 * Firm counts are deliberately absent site-wide. The reach claim pairs the
 * database with AI web discovery instead, which is the honest shape of the
 * product: a fixed floor plus an uncapped search.
 */
export const INVESTOR_COUNT_CLAIM = '5,000+';

/*
 * There is deliberately no WEB_DISCOVERY_CLAIM constant. One briefly existed
 * ('Unlimited') and was used as a stat tile beside the figure above. It was an
 * overclaim: plans reach 10, 50 or 150 investors a month (PRICING_TIERS in
 * types/index.ts), so a bare "Unlimited" in a stats row asserts something the
 * product does not do, which is the failure mode the claims policy in
 * .agents/product-marketing.md exists to prevent.
 *
 * What IS true is that the DISCOVERY POOL is not bounded by this table — the AI
 * also searches the live web per deck, so the database is the floor of reach and
 * not the ceiling. That claim needs its qualifier to stay true, so it lives in
 * prose (see the segment, stage and report pages) and never in a bare tile.
 */

/**
 * The numeric twin of approx(): the same rounded-down bucket, as a number, so
 * a server component can hand a client component a value that is already
 * rounded. Passing a raw count across the server/client boundary puts the exact
 * figure in the page payload even when the UI only prints approx(n). Anything
 * under the disclosure floor becomes 1, which approx() renders as 'under 25'.
 */
export function bucketCount(n: number): number {
  if (!Number.isFinite(n) || n <= 0) return 0;
  if (n < 25) return 1;
  const step = n >= 1000 ? 100 : n >= 100 ? 50 : 10;
  return Math.floor(n / step) * step;
}
